import type { EventRow } from "@rally/agreed";
import {
  Button,
  breakpoints,
  ChoiceChips,
  Col,
  DropdownMenu,
  Icon,
  IconButton,
  ListRow,
  MonthGrid,
  onWeb,
  Row,
  ScrollArea,
  SkeletonRows,
  Stack,
  Text,
  useTheme,
  type WeekEvent,
  WeekGrid,
} from "@rally/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";

import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";
import { goTo } from "../router.ts";
import { isTyping } from "../step.ts";
import { upcomingInView } from "./calendar-agenda.ts";
import { DAY, HOUR } from "./calendar-time.ts";
import { EventPanel } from "./event-panel.tsx";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const DAY_OPENS = 7;
const DAY_CLOSES = 22;

type ScheduledCall = {
  call: string;
  conversation: string;
  conversationTitle: string | null;
  scheduledAt: string;
};

function mondayOf(at: Date): Date {
  const day = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  return new Date(day.getTime() - ((day.getDay() + 6) % 7) * DAY);
}

function zoneSaid(now: Date): string {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(now);
  return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
}

const VIEWS = ["day", "week", "month"] as const;
type View = (typeof VIEWS)[number];

function dayStart(at: Date): Date {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate());
}

function stepped(view: View, anchor: Date, way: -1 | 1): Date {
  if (view === "day") return new Date(anchor.getTime() + way * DAY);
  if (view === "week") return new Date(anchor.getTime() + way * 7 * DAY);
  return new Date(anchor.getFullYear(), anchor.getMonth() + way, 1);
}

function spanOf(view: View, anchor: Date): { from: Date; to: Date } {
  if (view === "day") return { from: anchor, to: new Date(anchor.getTime() + DAY) };
  if (view === "week") {
    const monday = mondayOf(anchor);
    return { from: monday, to: new Date(monday.getTime() + 7 * DAY) };
  }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  return {
    from: new Date(first.getTime() - 7 * DAY),
    to: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 8),
  };
}

function calendarHeading(view: View, anchor: Date, days: Date[]): string {
  if (view === "day") {
    return anchor.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  }
  if (view === "month") return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return weekLabel(days);
}

function weekLabel(days: Date[]): string {
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return "";
  const month = first.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  if (first.getMonth() === last.getMonth()) return month;
  return `${first.toLocaleDateString(undefined, { month: "short" })} – ${last.toLocaleDateString(undefined, { month: "short", year: "numeric" })}`;
}

export function Calendar() {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const wide = !theme.isAtMost(breakpoints.tablet);
  const [view, setView] = useState<View>(() => (narrow ? "day" : "week"));
  const [anchor, setAnchor] = useState(() => dayStart(new Date()));
  const weekStart = view === "week" ? mondayOf(anchor) : anchor;
  const [spans, setSpans] = useState<EventRow[] | null>(null);
  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [form, setForm] = useState<{ at: Date; until?: Date; held: EventRow | null } | null>(null);
  const [wholeDay, setWholeDay] = useState(false);
  const [wrong, setWrong] = useState("");

  const span = spanOf(view, anchor);
  const from = new Date(span.from.getTime() - DAY).toISOString();
  const to = new Date(span.to.getTime() + DAY).toISOString();
  const again = useCallback(() => {
    void Promise.all([
      read<EventRow[]>("events.in", { from, to }),
      read<ScheduledCall[]>("schedule.upcoming", {}),
    ])
      .then(([events, scheduled]) => {
        setSpans(events);
        setCalls(scheduled.filter((call) => call.scheduledAt >= from && call.scheduledAt < to));
      })
      .catch(() => {
        setSpans([]);
        setCalls([]);
      });
  }, [from, to]);
  useEffect(again, [again]);

  const viewNow = useRef(view);
  viewNow.current = view;
  useEffect(() => {
    if (!onWeb || typeof document === "undefined") return;
    const onKey = (event: KeyboardEvent) => {
      const at = document.activeElement as HTMLElement | null;
      const page = document.querySelector('[data-testid="body-calendar"]');
      if (!at || !page?.contains(at) || isTyping(at)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "PageUp" || event.key === "PageDown") {
        event.preventDefault();
        setAnchor((was) => stepped(viewNow.current, was, event.key === "PageDown" ? 1 : -1));
        return;
      }
      if (event.key === "t" || event.key === "T") {
        event.preventDefault();
        setAnchor(dayStart(new Date()));
        return;
      }
      if (!/^Arrow(Up|Down|Left|Right)$/.test(event.key)) return;
      const blocks = Array.from(
        page.querySelectorAll<HTMLElement>('[data-testid="calendar-event"], [data-testid="calendar-call"]'),
      );
      if (blocks.length === 0) return;
      event.preventDefault();
      const here = blocks.findIndex((block) => block === at || block.contains(at));
      const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
      const next = here < 0 ? (forward ? 0 : blocks.length - 1) : forward ? here + 1 : here - 1;
      const to = blocks[Math.max(0, Math.min(blocks.length - 1, next))];
      to?.focus();
      to?.scrollIntoView({ block: "nearest" });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const here = spans ?? [];
  const days =
    view === "day"
      ? [anchor]
      : Array.from({ length: 7 }, (_, at) => new Date(weekStart.getTime() + at * DAY));
  const now = new Date();

  const busyHours = [
    ...here.map((span) => new Date(span.startsAt).getHours()),
    ...calls.map((call) => new Date(call.scheduledAt).getHours()),
  ];
  const todayShown = days.some((day) => dayStart(day).getTime() === dayStart(now).getTime());
  const opens = wholeDay ? 0 : Math.min(DAY_OPENS, ...busyHours, ...(todayShown ? [now.getHours()] : []));
  const closes = wholeDay ? 23 : Math.max(DAY_CLOSES, ...busyHours);

  const shown: WeekEvent[] = [
    ...here.map((span) => ({
      id: span.id,
      title: span.title,
      startsAt: span.startsAt,
      endsAt: span.endsAt,
      tone: "plan" as const,
      repeats: span.recurrence !== "none",
      allDay: span.allDay,
      ...(span.source === "google" ? { icon: "calendar" as const, tint: theme.color.successSoft } : {}),
    })),
    ...calls.map((call) => ({
      id: call.call,
      title: call.conversationTitle ?? say("schedule.untitled"),
      startsAt: call.scheduledAt,
      tone: "call" as const,
      icon: "video" as const,
      movable: false,
      whyFixed: say("calendar.callIsFixed"),
    })),
  ];

  function opened(event: WeekEvent) {
    const held = here.find((span) => span.id === event.id);
    if (held) setForm({ at: new Date(held.startsAt), held });
    else {
      const call = calls.find((one) => one.call === event.id);
      if (call) goTo(`/c/${call.conversation}`);
    }
  }

  function lifted(event: WeekEvent, startsAt: Date) {
    const held = here.find((span) => span.id === event.id);
    if (!held) return;
    setSpans(
      here.map((span) => (span.id === held.id ? { ...span, startsAt: startsAt.toISOString() } : span)),
    );
    void act("event.update", {
      event: held.id,
      title: held.title,
      startsAt: startsAt.toISOString(),
      endsAt: held.endsAt ?? new Date(new Date(held.startsAt).getTime() + HOUR).toISOString(),
      recurrence: held.recurrence,
      recurrenceEnd: held.recurrenceEnd,
    })
      .then(again)
      .catch(() => {
        setWrong(say("calendar.refused"));
        again();
      });
  }

  function resized(event: WeekEvent, endsAt: Date) {
    const held = here.find((span) => span.id === event.id);
    if (!held) return;
    setSpans(here.map((span) => (span.id === held.id ? { ...span, endsAt: endsAt.toISOString() } : span)));
    void act("event.update", {
      event: held.id,
      title: held.title,
      startsAt: held.startsAt,
      endsAt: endsAt.toISOString(),
      recurrence: held.recurrence,
      recurrenceEnd: held.recurrenceEnd,
    })
      .then(again)
      .catch(() => {
        setWrong(say("calendar.refused"));
        again();
      });
  }

  function moved(event: WeekEvent, startsAt: Date) {
    const held = here.find((span) => span.id === event.id);
    if (!held) return;
    const length = held.endsAt ? new Date(held.endsAt).getTime() - new Date(held.startsAt).getTime() : HOUR;
    setSpans(
      here.map((span) =>
        span.id === held.id
          ? {
              ...span,
              startsAt: startsAt.toISOString(),
              endsAt: new Date(startsAt.getTime() + length).toISOString(),
            }
          : span,
      ),
    );
    void act("event.update", {
      event: held.id,
      title: held.title,
      startsAt: startsAt.toISOString(),
      endsAt: new Date(startsAt.getTime() + length).toISOString(),
      recurrence: held.recurrence,
      recurrenceEnd: held.recurrenceEnd,
    })
      .then(again)
      .catch(() => {
        setWrong(say("calendar.refused"));
        again();
      });
  }

  return (
    <Stack gap={3} testID="body-calendar" style={{ flex: 1, minHeight: 0 }}>
      {narrow ? (
        <Row gap={1} align="center">
          <Button
            size="sm"
            variant="ghost"
            icon="chevron-left"
            accessibilityLabel={say("calendar.previous")}
            onPress={() => setAnchor(stepped(view, anchor, -1))}
          />
          <Text typeName="bodySemibold" style={{ flex: 1, textAlign: "center" }} numberOfLines={1}>
            {calendarHeading(view, anchor, days)}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            icon="chevron-right"
            accessibilityLabel={say("calendar.next")}
            onPress={() => setAnchor(stepped(view, anchor, 1))}
          />
          <Button
            size="sm"
            icon="plus"
            accessibilityLabel={say("calendar.new")}
            onPress={() => setForm({ at: nextHour(now), held: null })}
          />
          <DropdownMenu
            align="right"
            items={[
              {
                label: say("calendar.today"),
                icon: "calendar" as const,
                onPress: () => setAnchor(dayStart(new Date())),
              },
              ...(["day", "month"] as const).map((one) => ({
                label: say(`calendar.view.${one}`),
                icon: one === view ? ("check" as const) : ("calendar" as const),
                onPress: () => setView(one),
              })),
              {
                label: say("calendar.hours"),
                icon: wholeDay ? ("check" as const) : ("clock" as const),
                onPress: () => setWholeDay(!wholeDay),
              },
            ]}
            trigger={
              <Col style={{ pointerEvents: "none" }}>
                <IconButton icon="more-horizontal" ariaLabel={say("calendar.hours")} />
              </Col>
            }
          />
        </Row>
      ) : (
        <Row gap={2} align="center">
          <Button
            size="sm"
            variant="ghost"
            icon="chevron-left"
            accessibilityLabel={say("calendar.previous")}
            onPress={() => setAnchor(stepped(view, anchor, -1))}
          />
          <Button
            size="sm"
            variant="ghost"
            icon="chevron-right"
            accessibilityLabel={say("calendar.next")}
            onPress={() => setAnchor(stepped(view, anchor, 1))}
          />
          <Button
            size="sm"
            variant="outline"
            accessibilityLabel={say("calendar.today")}
            onPress={() => setAnchor(dayStart(new Date()))}
          >
            {say("calendar.today")}
          </Button>
          <Text typeName="title" style={{ marginLeft: theme.space[2] }} numberOfLines={1}>
            {calendarHeading(view, anchor, days)}
          </Text>
          <Col style={{ flex: 1 }} />
          <ChoiceChips
            value={view}
            onChange={setView}
            options={VIEWS.map((one) => ({
              value: one,
              label: say(`calendar.view.${one}`),
              testID: `calendar-view-${one}`,
            }))}
          />
          <Col style={{ flex: 1 }} />
          {view === "month" ? null : (
            <Button
              size="sm"
              variant={wholeDay ? "outline" : "ghost"}
              icon="clock"
              accessibilityLabel={say("calendar.hoursHint")}
              accessibilityState={{ selected: wholeDay }}
              onPress={() => setWholeDay(!wholeDay)}
            >
              {say("calendar.hours")}
            </Button>
          )}
          <Button
            size="sm"
            icon="plus"
            accessibilityLabel={say("calendar.new")}
            onPress={() => setForm({ at: nextHour(now), held: null })}
          >
            {say("calendar.new")}
          </Button>
        </Row>
      )}

      {wrong ? (
        <Text typeName="caption" color="danger" accessibilityLiveRegion="polite">
          {wrong}
        </Text>
      ) : null}

      <Row style={{ flex: 1, minHeight: 0 }} align="stretch">
        <Col style={{ flex: 1, minWidth: 0 }}>
          {spans === null ? (
            <SkeletonRows rows={6} />
          ) : view === "month" ? (
            <MonthGrid
              month={anchor}
              events={shown}
              now={now}
              testID="calendar-month"
              onPickDay={(day) => {
                setAnchor(dayStart(day));
                setView("day");
              }}
              onOpen={opened}
            />
          ) : (
            <WeekGrid
              days={days}
              opens={opens}
              closes={closes}
              events={shown}
              now={now}
              testID="calendar-week"
              dayLabel={(day) => say(`calendar.day.${DAY_KEYS[(day.getDay() + 6) % 7] ?? "mon"}`)}
              onPick={(at) => setForm({ at, held: null })}
              onPickRange={(from, to) => setForm({ at: from, until: to, held: null })}
              onResize={resized}
              onResizeStart={lifted}
              zone={narrow ? undefined : zoneSaid(now)}
              startAtNow
              emptyLabel={say("calendar.emptyHint")}
              onOpen={opened}
              onMove={moved}
            />
          )}
        </Col>
        {wide ? (
          <Agenda
            events={shown}
            from={view === "month" ? new Date(anchor.getFullYear(), anchor.getMonth(), 1) : span.from}
            to={view === "month" ? new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1) : span.to}
            now={now}
            onOpen={opened}
          />
        ) : null}
      </Row>

      {form ? (
        <EventPanel
          at={form.at}
          {...(form.until ? { until: form.until } : {})}
          held={form.held}
          onDone={() => {
            setForm(null);
            again();
          }}
          onClose={() => setForm(null)}
        />
      ) : null}
    </Stack>
  );
}

function Agenda({
  events,
  from,
  to,
  now,
  onOpen,
}: {
  events: WeekEvent[];
  from: Date;
  to: Date;
  now: Date;
  onOpen: (event: WeekEvent) => void;
}) {
  const theme = useTheme();
  const ahead = upcomingInView(events, from, to, now);
  const byDay = new Map<string, WeekEvent[]>();
  for (const event of ahead) {
    const key = dayStart(new Date(event.startsAt)).toISOString();
    byDay.set(key, [...(byDay.get(key) ?? []), event]);
  }
  return (
    <Col
      testID="calendar-agenda"
      style={{
        width: 240,
        borderLeftWidth: 1,
        borderLeftColor: theme.color["border.default"],
        paddingLeft: theme.space[4],
        marginLeft: theme.space[4],
      }}
    >
      <Text typeName="label" color="text.secondary" style={{ paddingBottom: theme.space[2] }}>
        {say("calendar.upcoming")}
      </Text>
      {ahead.length === 0 ? (
        <Text typeName="caption" color="text.tertiary">
          {say("calendar.nothingAhead")}
        </Text>
      ) : (
        <ScrollArea style={{ flex: 1 }}>
          {[...byDay.entries()].map(([key, list]) => (
            <Col key={key} style={{ paddingBottom: theme.space[3] }}>
              <Text typeName="caption" color="text.tertiary" style={{ paddingBottom: theme.space[1] }}>
                {new Date(key).toLocaleDateString(undefined, {
                  weekday: "long",
                  day: "numeric",
                  month: "short",
                })}
              </Text>
              {list.map((event, at) => (
                <ListRow
                  key={`${event.id}-${event.startsAt}`}
                  title={event.title}
                  value={
                    event.allDay
                      ? say("calendar.field.allDay")
                      : new Date(event.startsAt).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hourCycle: "h23",
                        })
                  }
                  {...(event.icon
                    ? { lead: <Icon name={event.icon} size={14} color="text.tertiary" /> }
                    : {})}
                  last={at === list.length - 1}
                  onPress={() => onOpen(event)}
                  testID="calendar-agenda-row"
                />
              ))}
            </Col>
          ))}
        </ScrollArea>
      )}
    </Col>
  );
}

function nextHour(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1);
}
